import { useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { apiGet, getApiError, type PaginatedResult } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { Card } from '../../components/Card';
import { PageScaffold } from '../../components/PageScaffold';
import { DataTableCard, STICKY_HEAD } from '../../components/DataTable';
import { Money } from '../../components/Money';
import { StatusPill, orderStatusTone, paymentStatusTone } from '../../components/StatusPill';
import { Button } from '../../components/Button';
import { EmptyState } from '../../components/EmptyState';
import { Pagination } from '../../components/Pagination';
import { DownloadReportModal } from '../../components/DownloadReportModal';
import { cn } from '../../lib/cn';
import { rowNav } from '../../lib/rowNav';

const PAGE_SIZE = 20;

interface OrderRow {
  id: string;
  orderNo: string;
  number: string;
  status: string;
  paymentStatus: string;
  total: string;
  balanceDue: string;
  createdAt: string;
  // LEGACY = a paper order written before the 1 Jul 2026 go-live, back-entered
  // so its production/delivery/invoice leg runs through the system.
  origin?: 'SYSTEM' | 'LEGACY';
  legacyOrderDate?: string | null;
  customer: {
    id: string;
    type: string;
    name: string;
    companyName: string | null;
    contactPerson: string | null;
    phone: string;
  };
  shop: { id: string; code: string; name: string };
  salesperson: { id: string; name: string };
}

interface Shop {
  id: string;
  code: string;
  name: string;
  isActive: boolean;
}

const STATUSES_ALL = ['DRAFT', 'CONFIRMED', 'READY', 'DELIVERED', 'COMPLETED', 'CANCELLED'];
// DRAFTs are in-progress carts — hidden from view-only personas (finance, viewer).
const STATUSES_VIEW_ONLY = ['CONFIRMED', 'READY', 'DELIVERED', 'COMPLETED', 'CANCELLED'];

export function OrdersListPage(): JSX.Element {
  const { perms } = useAuth();
  const canCreate = perms.has('orders.create');
  const canSeeAllShops = perms.has('orders.view_all_shops');
  const navigate = useNavigate();
  // All filters live in the URL query string, not local state. That keeps the
  // filtered view shareable/bookmarkable and — crucially — means the browser
  // back button restores the exact filters when returning from an order.
  const [searchParams, setSearchParams] = useSearchParams();
  const status = searchParams.get('status');
  const q = searchParams.get('q') ?? '';
  const payment = searchParams.get('payment') ?? '';
  const dateRange = searchParams.get('range') ?? 'all';
  const specificDate = searchParams.get('date') ?? '';
  const sort = searchParams.get('sort') ?? 'newest';
  const pageParam = Number(searchParams.get('page') ?? '1');
  const page = Number.isSafeInteger(pageParam) && pageParam > 0 ? pageParam : 1;
  const shopId = searchParams.get('shopId');

  // Merge a patch into the URL. Empty/null values are dropped to keep the query
  // string clean; any filter change snaps back to page 1. `replace` so typing in
  // the search box doesn't flood browser history with one entry per keystroke.
  const patchParams = (patch: Record<string, string | null>, resetPage = true): void => {
    const next = new URLSearchParams(searchParams);
    for (const [key, value] of Object.entries(patch)) {
      if (value === null || value === '') next.delete(key);
      else next.set(key, value);
    }
    if (resetPage) next.delete('page');
    setSearchParams(next, { replace: true });
  };
  const setStatus = (s: string | null): void => patchParams({ status: s });
  const setQ = (v: string): void => patchParams({ q: v });
  const setPayment = (v: string): void => patchParams({ payment: v });
  // Switching away from "specific" drops the stale date param so it isn't sent.
  const setDateRange = (v: string): void =>
    patchParams({ range: v === 'all' ? null : v, ...(v === 'specific' ? {} : { date: null }) });
  const setSpecificDate = (v: string): void => patchParams({ date: v || null });
  const setSort = (v: string): void => patchParams({ sort: v === 'newest' ? null : v });
  const setPage = (p: number): void => patchParams({ page: String(p) }, false);

  const shops = useQuery({
    queryKey: ['shops'],
    queryFn: () => apiGet<Shop[]>('/shops'),
    enabled: canSeeAllShops,
  });

  // Send every date filter to the server before counting/paging the results.
  const [dateFrom, dateTo] = useMemo((): [string | null, string | null] => {
    const now = new Date();
    switch (dateRange) {
      case 'today':
        now.setHours(0, 0, 0, 0);
        return [now.toISOString(), null];
      case '7d':
        return [new Date(now.getTime() - 7 * 86_400_000).toISOString(), null];
      case '30d':
        return [new Date(now.getTime() - 30 * 86_400_000).toISOString(), null];
      case 'month':
        return [new Date(now.getFullYear(), now.getMonth(), 1).toISOString(), null];
      case 'specific': {
        const [y, m, d] = specificDate.split('-').map(Number);
        if (!y || !m || !d) return [null, null];
        return [
          new Date(y, m - 1, d, 0, 0, 0, 0).toISOString(),
          new Date(y, m - 1, d, 23, 59, 59, 999).toISOString(),
        ];
      }
      default:
        return [null, null];
    }
  }, [dateRange, specificDate]);

  // Search runs SERVER-SIDE so it spans every order, not just the recent page the
  // list ships by default — the backend matches order no., pro forma no. and VAT
  // invoice no. (plus customer name / company / phone). A 2-char floor mirrors the
  // customers lookup and avoids a request on the first keystroke.
  const searchQ = q.trim().length >= 2 ? q.trim() : '';

  const orders = useQuery({
    queryKey: ['orders', { status, shopId, dateFrom, dateTo, q: searchQ, payment, sort, page }],
    queryFn: () =>
      apiGet<PaginatedResult<OrderRow>>('/orders', {
        page,
        pageSize: PAGE_SIZE,
        sort,
        ...(status ? { status } : {}),
        ...(shopId ? { shopId } : {}),
        ...(dateFrom ? { from: dateFrom } : {}),
        ...(dateTo ? { to: dateTo } : {}),
        ...(payment ? { paymentStatus: payment } : {}),
        ...(searchQ ? { q: searchQ } : {}),
      }),
  });

  const setShop = (id: string | null): void => patchParams({ shopId: id });

  const [showReportModal, setShowReportModal] = useState(false);

  const activeShop =
    canSeeAllShops && shopId ? (shops.data ?? []).find((s) => s.id === shopId) : null;

  const rows = orders.data?.rows ?? [];
  const total = orders.data?.total ?? 0;

  const hasFilters = !!(q || payment || dateRange !== 'all' || sort !== 'newest' || status);
  const clearFilters = (): void =>
    patchParams({ status: null, q: null, payment: null, range: null, date: null, sort: null });

  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const safePage = orders.data?.page ?? page;

  return (
    <PageScaffold
      scroll={false}
      title={
        <>
          Orders
          {activeShop && (
            <span className="text-ink-3 font-normal text-2xl ml-2">
              · {activeShop.code} {activeShop.name}
            </span>
          )}
        </>
      }
      subtitle={
        canSeeAllShops
          ? 'Every order across all shops. Filter by shop or status; click any to view detail.'
          : 'Sales in your shop. Click any to view, edit, or take payment.'
      }
      actions={
        <div className="flex items-center gap-2">
          <Button variant="secondary" size="sm" onClick={() => setShowReportModal(true)}>
            Download report · Excel
          </Button>
          {canCreate && (
            <Link to="/orders/new">
              <Button>+ New order</Button>
            </Link>
          )}
        </div>
      }
    >
      <Card className="shrink-0" bodyClassName="p-4 space-y-3">
        {canSeeAllShops && (
          <div className="flex flex-wrap gap-1.5 items-center">
            <span className="text-xs uppercase tracking-wide text-ink-3 mr-2">Shop</span>
            <Chip active={shopId === null} onClick={() => setShop(null)}>
              All shops
            </Chip>
            {(shops.data ?? [])
              .filter((s) => s.isActive)
              .map((s) => (
                <Chip key={s.id} active={shopId === s.id} onClick={() => setShop(s.id)}>
                  {s.code}
                </Chip>
              ))}
          </div>
        )}
        <div className="flex flex-wrap gap-1.5 items-center">
          <span className="text-xs uppercase tracking-wide text-ink-3 mr-2">Status</span>
          <Chip active={status === null} onClick={() => setStatus(null)}>
            All
          </Chip>
          {(canCreate ? STATUSES_ALL : STATUSES_VIEW_ONLY).map((s) => (
            <Chip key={s} active={status === s} onClick={() => setStatus(s)}>
              {s}
            </Chip>
          ))}
        </div>

        {/* Search · payment · date · sort */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end pt-2 border-t border-rule-2">
          <div className="md:col-span-5">
            <FilterField label="Search">
              <input
                className="kc-input"
                placeholder="Order no., pro forma no., VAT invoice no., customer, or phone…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
              />
            </FilterField>
          </div>
          <FilterSelect
            label="Payment"
            value={payment}
            onChange={setPayment}
            className="md:col-span-2"
          >
            <option value="">All</option>
            <option value="PAID">Paid</option>
            <option value="PARTIAL">Partial</option>
            <option value="UNPAID">Unpaid</option>
          </FilterSelect>
          <FilterSelect
            label="Date"
            value={dateRange}
            onChange={setDateRange}
            className="md:col-span-2"
          >
            <option value="all">All time</option>
            <option value="today">Today</option>
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="month">This month</option>
            <option value="specific">Specific date…</option>
          </FilterSelect>
          <FilterSelect label="Sort" value={sort} onChange={setSort} className="md:col-span-3">
            <option value="newest">Newest first</option>
            <option value="oldest">Oldest first</option>
            <option value="total_desc">Total: high → low</option>
            <option value="total_asc">Total: low → high</option>
            <option value="balance_desc">Balance: high → low</option>
          </FilterSelect>
          {dateRange === 'specific' && (
            <div className="md:col-span-3">
              <FilterField label="On date">
                <input
                  type="date"
                  className="kc-input"
                  value={specificDate}
                  max={new Date().toLocaleDateString('en-CA')}
                  onChange={(e) => setSpecificDate(e.target.value)}
                />
              </FilterField>
            </div>
          )}
        </div>
        <div className="flex items-center justify-between text-xs text-ink-3">
          <span>
            {total} order{total === 1 ? '' : 's'}
          </span>
          {hasFilters && (
            <Button variant="tertiary" size="xs" onClick={clearFilters}>
              Clear all filters
            </Button>
          )}
        </div>
      </Card>

      <DataTableCard
        footer={
          total > 0 ? (
            <Pagination
              page={safePage}
              pageCount={pageCount}
              total={total}
              pageSize={PAGE_SIZE}
              onChange={setPage}
              unit="order"
            />
          ) : undefined
        }
      >
        {orders.isLoading ? (
          <div className="px-6 py-12 text-center text-sm text-ink-3">Loading…</div>
        ) : orders.isError ? (
          <EmptyState
            title="Could not load orders"
            description={getApiError(orders.error).message}
          />
        ) : rows.length === 0 ? (
          <EmptyState
            title="No orders match"
            description="Try a different filter or search."
            action={
              hasFilters ? (
                <Button variant="secondary" size="sm" onClick={clearFilters}>
                  Clear filters
                </Button>
              ) : undefined
            }
          />
        ) : (
          <table className="w-full text-sm">
            <thead className={STICKY_HEAD}>
              <tr className="text-left text-xs uppercase tracking-wide text-ink-3 border-b border-rule">
                <th className="px-6 py-2 font-medium">Order no.</th>
                <th className="px-6 py-2 font-medium">Customer</th>
                <th className="px-6 py-2 font-medium">Shop</th>
                <th className="px-6 py-2 font-medium">Status</th>
                <th className="px-6 py-2 font-medium">Payment</th>
                <th className="px-6 py-2 font-medium text-right">Total</th>
                <th className="px-6 py-2 font-medium text-right">Balance</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((o) => (
                <tr
                  key={o.id}
                  onClick={rowNav(navigate, `/orders/${o.id}`)}
                  className="border-b border-rule-2 hover:bg-paper-2 cursor-pointer"
                >
                  <td className="px-6 py-3">
                    <Link to={`/orders/${o.id}`} className="kc-mono text-ink hover:text-accent">
                      {o.orderNo}
                    </Link>
                    {/* Proforma / invoice doc number as secondary reference — hidden
                        while it's still the internal DRAFT-… placeholder. */}
                    {!o.number.startsWith('DRAFT-') && (
                      <div className="text-xs text-ink-3 kc-mono mt-0.5">{o.number}</div>
                    )}
                    {o.origin === 'LEGACY' && (
                      <span className="inline-block mt-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium bg-warning-soft text-warning">
                        LEGACY
                      </span>
                    )}
                    {/* For a legacy order the createdAt below is the back-entry
                        instant, which tells the reader nothing — show the date the
                        sale was actually booked on paper instead. */}
                    <div className="text-xs text-ink-3 mt-0.5">
                      {o.origin === 'LEGACY' && o.legacyOrderDate
                        ? new Date(o.legacyOrderDate).toLocaleDateString()
                        : new Date(o.createdAt).toLocaleString()}
                    </div>
                  </td>
                  <td className="px-6 py-3">
                    <div className="text-ink">
                      {o.customer.type === 'company'
                        ? (o.customer.companyName ?? o.customer.name)
                        : o.customer.name}
                    </div>
                    {o.customer.type === 'company' && (
                      <div className="text-xs text-ink-3">
                        Contact: {o.customer.contactPerson ?? o.customer.name}
                      </div>
                    )}
                    <div className="text-xs text-ink-3">{o.customer.phone}</div>
                  </td>
                  <td className="px-6 py-3">
                    <StatusPill>{o.shop.code}</StatusPill>
                  </td>
                  <td className="px-6 py-3">
                    <StatusPill tone={orderStatusTone(o.status)}>{o.status}</StatusPill>
                  </td>
                  <td className="px-6 py-3">
                    <StatusPill tone={paymentStatusTone(o.paymentStatus)}>
                      {o.paymentStatus}
                    </StatusPill>
                  </td>
                  <td className="px-6 py-3 text-right">
                    <Money value={o.total} />
                  </td>
                  <td className="px-6 py-3 text-right">
                    <Money value={o.balanceDue} muted={o.balanceDue === '0'} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </DataTableCard>

      {showReportModal && (
        <DownloadReportModal
          title="Download orders report"
          endpoint="/orders/export.xlsx"
          filenamePrefix="orders-report"
          showShop={canSeeAllShops}
          onClose={() => setShowReportModal(false)}
        />
      )}
    </PageScaffold>
  );
}

function FilterField({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs font-medium text-ink-3 uppercase tracking-wide">{label}</span>
      {children}
    </label>
  );
}

function FilterSelect({
  label,
  value,
  onChange,
  className,
  children,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  className?: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <label className={cn('flex flex-col gap-1', className)}>
      <span className="text-xs font-medium text-ink-3 uppercase tracking-wide">{label}</span>
      <select value={value} onChange={(e) => onChange(e.target.value)} className="kc-input">
        {children}
      </select>
    </label>
  );
}

function Chip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <button
      onClick={onClick}
      className={cn(
        'px-3 py-1.5 rounded text-xs uppercase tracking-wide transition border',
        active
          ? 'bg-accent text-white border-accent'
          : 'bg-paper-2 text-ink-2 border-rule hover:border-accent/40',
      )}
    >
      {children}
    </button>
  );
}
